import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar from './EmployerSidebar';
import Autocomplete from './AutoComplete';
import { CITIES_LIST } from '../data/Autocompletedata';

// Builds a blank form, pre-filling company_name from the logged-in employer's
// own profile (the `name` field on the user/profile record) so they never
// have to type it manually. Takes `user` as a parameter (rather than reading
// a module-level `user`) so it works whether it's called from useState's
// initializer or from an event handler.
const buildEmptyForm = (user) => ({
  title: '', description: '', company_name: user?.name || '', job_category_id: '', location: '',
  work_mode: 'office', job_type: 'full_time', work_shift: 'day',
  skills_required: '', salary_min: '', salary_max: '',
  experience_required: '', openings: '', benefits: '',
  contact_number: '',
  gender: 'both', marital_status: 'both', education: '', qualification: '',
  english_level: 'not_required', age_min: '', age_max: '',
});

// Builds a reasonable draft description from the other fields already
// filled in the form. Employers can edit the result afterward — this is
// just a starting point, not a final copy.
const generateJobDescription = (data) => {
  const {
    title, company_name, location, work_mode, job_type, work_shift,
    skills_required, experience_required, salary_min, salary_max,
    openings, benefits,
  } = data;

  if (!title) return '';

  const workModeLabel = { office: 'in-office', hybrid: 'hybrid', work_from_home: 'remote' }[work_mode] || work_mode;
  const jobTypeLabel = { full_time: 'a full-time', part_time: 'a part-time', internship: 'an internship', contract: 'a contract' }[job_type] || job_type;
  const shiftLabel = work_shift === 'night' ? 'night shift' : 'day shift';

  let lines = [];

  lines.push(
    `${company_name || 'We'} ${company_name ? 'is' : 'are'} hiring for the position of ${title}${location ? ` in ${location}` : ''}. This is ${jobTypeLabel} role, ${workModeLabel}, working ${shiftLabel}.`
  );

  if (experience_required) {
    lines.push(`We're looking for candidates with around ${experience_required} year${experience_required == 1 ? '' : 's'} of relevant experience.`);
  }

  if (skills_required) {
    lines.push(`Key skills required: ${skills_required}.`);
  }

  if (salary_min || salary_max) {
    const salaryText = salary_min && salary_max
      ? `₹${salary_min} - ₹${salary_max}`
      : `₹${salary_min || salary_max}`;
    lines.push(`Compensation: ${salaryText} per annum, based on experience and fit.`);
  }

  if (openings) {
    lines.push(`We currently have ${openings} opening${openings == 1 ? '' : 's'} for this role.`);
  }

  if (benefits) {
    lines.push(`Benefits include: ${benefits}.`);
  }

  lines.push("If this sounds like a good fit, we'd love to hear from you — apply today!");

  return lines.join('\n\n');
};

const CLOSE_REASONS = [
  'Position filled',
  'Sufficient applications received',
  'Hiring postponed',
  'Position cancelled',
  'Budget constraints',
  'Recruitment on hold',
  'Internal candidate selected',
  'Duplicate job posting',
  'Other',
];

const GRADIENT_BUTTON =
  "bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white font-semibold rounded-[18px] shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 backdrop-blur-md border border-white/20";

// Same gradient as GRADIENT_BUTTON, reused as text (bg-clip-text) for
// headings like "All Jobs (5)" so the accent color stays consistent.
const GRADIENT_TEXT =
  "bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent";

const STATUS_BADGE_STYLES = {
  active: 'bg-[#ECFDF5] text-[#10B981]',
  paused: 'bg-[#FFF7ED] text-[#F59E0B]',
  closed: 'bg-gray-100 text-gray-500',
  expired: 'bg-[#FEF2F2] text-[#EF4444]',
};

// Background/text color for the status <select> itself, so it reads as a
// filled pill (blue when Active, matching the reference dropdown) rather
// than a plain bordered box.
const STATUS_SELECT_STYLES = {
  active: 'bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] border-transparent text-white shadow-sm shadow-purple-500/25',
  paused: 'bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] border-transparent text-white shadow-sm shadow-purple-500/25',
  closed: 'bg-white border-gray-200 text-gray-600',
};

// Inline colors for the <option> elements inside that select — browsers
// that support styling native option backgrounds (Chrome/Edge) will show
// the currently-selected option highlighted to match. Native <option>
// elements can't render a gradient, so Active/Paused use a solid color
// sampled from the gradient instead.
const STATUS_OPTION_COLORS = {
  active: { backgroundColor: '#8B5CF6', color: '#ffffff' },
  paused: { backgroundColor: '#8B5CF6', color: '#ffffff' },
  closed: { backgroundColor: '#ffffff', color: '#2563EB' },
};

// Accent color per plan tier — reused for the sidebar plan avatar so it
// reads consistently with the job-seeker profile ring's color-by-tier idea.
const PLAN_ACCENT = {
  free: '#94A3B8',
  basic: '#2563EB',
  pro: '#7C3AED',
  enterprise: '#10B981',
};

// --- Small inline icon set (keeps this file dependency-free) ---
const FunnelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polygon points="4 4 20 4 14 12.5 14 19 10 21 10 12.5 4 4" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronDownIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronRightIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const RepostIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const BriefcaseNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const DatabaseNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const UsersNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BarChartNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const HelpNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const PhoneNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

function SidebarNavItem({ icon, label, to, active, disabled }) {
  const content = (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
        active
          ? 'bg-[#ECFDF5] text-[#0F8B6D]'
          : disabled
          ? 'text-gray-300 cursor-not-allowed'
          : 'text-gray-600 hover:bg-gray-50'
      }`}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {disabled && <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Soon</span>}
    </div>
  );

  if (disabled) return content;
  return <Link to={to}>{content}</Link>;
}

// Reusable pill/chip filter button. Shows a plain "+" when inactive and
// a filled state with an "x" (to clear) when active, so it's obvious at a
// glance which filter is currently applied. Active pills use the same
// purple-to-pink gradient as the primary buttons on this page.
function FilterPill({ icon, label, count, active, onClick, clearable = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors
        ${active
          ? 'border-transparent bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white shadow-md shadow-purple-500/25'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
    >
      {active && clearable ? <XIcon /> : icon}
      <span>
        {label}
        {count !== undefined && ` (${count})`}
      </span>
    </button>
  );
}

// Right-sidebar "Current Plan" card — the employer-side equivalent of the
// job-seeker's profile-completion card in JobSearch.jsx. Reads the same
// shape of data EmployerBilling.jsx already consumes from
// /company/subscription (plan_name, expires_at).
// Right-sidebar "Current Plan" card. Gradient header with the plan name,
// a floating icon badge, a status pill, a days-left line, and a CTA.
const PLAN_ICONS = {
  free: '🌱',
  starter: '🚀',
  basic: '🚀',
  growth: '📈',
  pro: '👑',
  enterprise: '🏢',
};

function CurrentPlanCard({ planInfo, planPrice }) {
  // null = still loading, false = fetch failed
  if (planInfo === null) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden animate-pulse">
        <div className="h-28 bg-gray-100" />
        <div className="p-6 space-y-3">
          <div className="h-3 bg-gray-100 rounded w-2/3 mx-auto" />
          <div className="h-9 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const planName =
    planInfo && planInfo.plan_name && planInfo.plan_name !== 'free'
      ? planInfo.plan_name
      : 'free';
  const isFree = planName === 'free';
  const label = planName.charAt(0).toUpperCase() + planName.slice(1);
  const icon = PLAN_ICONS[planName.toLowerCase()] || '⭐';

  const expires = !isFree && planInfo?.expires_at ? new Date(planInfo.expires_at) : null;
  const expiresText = expires
    ? expires.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const daysLeft = expires
    ? Math.ceil((expires.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const expiringSoon = daysLeft !== null && daysLeft <= 7;

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-purple-500/10 border border-purple-100 overflow-hidden">
      {/* Gradient header */}
      <div
        className={`relative px-6 pt-6 pb-12 text-center text-white ${
          isFree
            ? 'bg-gradient-to-br from-[#64748B] via-[#94A3B8] to-[#CBD5E1]'
            : 'bg-gradient-to-br from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D]'
        }`}
      >
        {/* decorative circles */}
        <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/10" />
        <div className="absolute -bottom-10 -left-6 w-24 h-24 rounded-full bg-white/10" />

        <p className="relative text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">
          Current Plan
        </p>
        <h4 className="relative mt-1 text-2xl font-extrabold">
          {isFree ? 'Free' : label}
        </h4>
      </div>

      {/* Floating icon badge */}
      <div className="relative z-10 -mt-8 flex justify-center">
        <div className="w-16 h-16 rounded-full bg-white shadow-lg ring-4 ring-white flex items-center justify-center text-3xl">
          {icon}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 pb-6 pt-3 text-center">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full ${
            isFree
              ? 'bg-gray-100 text-gray-500'
              : expiringSoon
              ? 'bg-[#FFF7ED] text-[#F59E0B]'
              : 'bg-[#ECFDF5] text-[#10B981]'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              isFree ? 'bg-gray-400' : expiringSoon ? 'bg-[#F59E0B]' : 'bg-[#10B981]'
            }`}
          />
          {isFree ? 'Free tier' : expiringSoon ? 'Expiring soon' : 'Active'}
        </span>

        {!isFree && planPrice > 0 && (
          <p className="mt-3 text-3xl font-extrabold bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">
            ₹{planPrice.toLocaleString('en-IN')}
            <span className="text-sm font-medium text-gray-400"> / month</span>
          </p>
        )}

        {isFree ? (
          <p className="mt-3 text-sm text-gray-500">
            Unlock more job posts, candidate credits and boosts.
          </p>
        ) : (
          expiresText && (
            <div className="mt-3 rounded-xl bg-[#F5F3FF] px-4 py-3">
              <p className="text-xs text-gray-500">Renews on</p>
              <p className="text-sm font-bold text-[#0D1B3D]">{expiresText}</p>
              {daysLeft !== null && (
                <p className={`text-xs font-semibold mt-0.5 ${expiringSoon ? 'text-[#F59E0B]' : 'text-[#7C3AED]'}`}>
                  {daysLeft <= 0 ? 'Renews today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                </p>
              )}
            </div>
          )
        )}

            <Link
          to="/employer/settings/billing"
          className={`mt-4 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm ${GRADIENT_BUTTON}`}
        >
          {isFree ? '🚀 Upgrade Plan' : 'Manage Billing'}
          <ChevronRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

// Job card matching the apnaHire "All Jobs" layout: title + status badge,
// location/posted-date meta line, two clickable stat blocks (Applied to
// job / Database Matches) with chevrons, and a "Repost now" action (button
// + bottom banner strip) for expired/closed jobs instead of the status
// dropdown.
const JOB_TYPE_LABELS = { full_time: 'Full Time', part_time: 'Part Time', internship: 'Internship', contract: 'Contract' };
const WORK_MODE_LABELS = { office: 'Office', hybrid: 'Hybrid', work_from_home: 'Work From Home' };

function JobCard({ job, user, onEdit, onDelete, onStatusChange, onRepost, onTogglePin, onFindCandidates, fcLoadingJobId, onBoost, boostingJobId, boostsAvailable }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isExpired = job.status === 'expired' || job.status === 'closed';

  const postedDate = job.created_at
    ? new Date(job.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const salaryText =
    job.salary_min && job.salary_max
      ? `₹${job.salary_min} - ₹${job.salary_max}`
      : job.salary_min || job.salary_max
      ? `₹${job.salary_min || job.salary_max}`
      : null;

  const chips = [
    JOB_TYPE_LABELS[job.job_type],
    WORK_MODE_LABELS[job.work_mode],
    salaryText,
    job.openings ? `${job.openings} opening${job.openings == 1 ? '' : 's'}` : null,
  ].filter(Boolean);

  return (
    <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-purple-200 transition-all duration-200 overflow-hidden">
      {/* Accent bar */}
      <div className={`h-1 w-full ${isExpired ? 'bg-gray-200' : 'bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D]'}`} />

      <div className="p-4 sm:p-5">
        {/* Header: title + badge | pin + menu */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-[#0D1B3D] text-lg leading-tight truncate">{job.title}</h3>
              <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${STATUS_BADGE_STYLES[job.status] || STATUS_BADGE_STYLES.closed}`}>
                {job.status}
              </span>
              {job.is_featured_active && (
                <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#F3E8FF] text-[#9333EA]">
                  ⚡ Boosted
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              <span className="inline-flex items-center gap-1">📍 {job.location || 'Location not specified'}</span>
              {postedDate && <span className="inline-flex items-center gap-1">🗓️ Posted {postedDate}</span>}
              {job.contact_name && <span className="inline-flex items-center gap-1">👤 {job.contact_name}</span>}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => onTogglePin(job.id, !job.pinned)}
              aria-label={job.pinned ? 'Unpin job' : 'Pin job'}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition ${
                job.pinned ? 'bg-yellow-50 text-yellow-500' : 'text-gray-300 hover:bg-gray-50 hover:text-yellow-500'
              }`}
            >
              {job.pinned ? '\u2b50' : '\u2606'}
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((m) => !m)}
                aria-label="More actions"
                className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <circle cx="12" cy="5" r="1.8" />
                  <circle cx="12" cy="12" r="1.8" />
                  <circle cx="12" cy="19" r="1.8" />
                </svg>
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
                    <button
                      onClick={() => { onEdit(job); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => { onDelete(job.id); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Detail chips */}
        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {chips.map((c, i) => (
              <span key={i} className="text-xs font-medium text-gray-600 bg-gray-50 border border-gray-100 rounded-full px-3 py-1">
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Footer: stats + actions | status */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/employer/jobs/${job.id}/applicants`}
              className="group inline-flex items-center gap-2 rounded-lg bg-[#F5F3FF] hover:bg-[#EDE9FE] px-3 py-2 transition"
            >
              <span className="text-base font-bold text-[#0D1B3D]">{job.applications_count ?? 0}</span>
              <span className="text-xs font-medium text-gray-600">Applicants</span>
              <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <button
              type="button"
              onClick={() => onFindCandidates?.(job)}
              disabled={fcLoadingJobId === job.id}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg bg-[#7C3AED]/10 text-[#7C3AED] hover:bg-[#7C3AED]/20 disabled:opacity-50 whitespace-nowrap transition"
            >
              {fcLoadingJobId === job.id ? 'Opening...' : '🔍 Find Candidates'}
            </button>

            {job.status === 'active' && !job.is_featured_active && boostsAvailable > 0 && (
              <button
                type="button"
                onClick={() => onBoost?.(job.id)}
                disabled={boostingJobId === job.id}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white hover:opacity-90 disabled:opacity-50 whitespace-nowrap transition"
              >
                {boostingJobId === job.id ? 'Boosting...' : '⚡ Boost'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 lg:justify-end">
            {isExpired ? (
              <button
                onClick={() => onRepost?.(job.id)}
                className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 ${GRADIENT_BUTTON}`}
              >
                <RepostIcon />
                Repost now
              </button>
            ) : (
              <div className="relative">
                <select
                  value={job.status}
                  onChange={(e) => onStatusChange(job.id, e.target.value)}
                  className={`appearance-none text-xs font-semibold rounded-lg pl-3 pr-8 py-2.5 border cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/30 ${
                    STATUS_SELECT_STYLES[job.status] || STATUS_SELECT_STYLES.closed
                  }`}
                >
                  <option value="active" style={STATUS_OPTION_COLORS.active}>Active</option>
                  <option value="paused" style={STATUS_OPTION_COLORS.paused}>Paused</option>
                  <option value="closed" style={STATUS_OPTION_COLORS.closed}>Closed</option>
                </select>
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${
                    job.status === 'active' || job.status === 'paused' ? 'text-white' : 'text-gray-400'
                  }`}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// "All Filters" modal — matches the apnaHire filter dialog: collapsible
// Status and Location sections, each a multi-select checkbox list, with
// Location options built live from the employer's own posted jobs (no
// hardcoded cities). Clear all resets both filter arrays; Apply just
// closes the modal since filtering already happens live as checkboxes
// are toggled.
function AllFiltersModal({
  show, onClose,
  statusFilters, onToggleStatus,
  locationFilters, onToggleLocation,
  locationOptions,
  onClearAll,
}) {
  const [statusOpen, setStatusOpen] = useState(true);
  const [locationOpen, setLocationOpen] = useState(true);

  if (!show) return null;

  const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'closed', label: 'Closed' },
    { value: 'expired', label: 'Expired' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#0D1B3D]">All filters</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Status section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setStatusOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-[#0D1B3D]"
            >
              Status
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusOpen && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                {STATUS_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusFilters.includes(opt.value)}
                      onChange={() => onToggleStatus(opt.value)}
                      className="rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]/30"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Location section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setLocationOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-[#0D1B3D]"
            >
              Location
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${locationOpen ? 'rotate-180' : ''}`} />
            </button>
            {locationOpen && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                {locationOptions.length === 0 ? (
                  <p className="text-sm text-gray-400 py-1">No locations yet — post a job to see options here.</p>
                ) : (
                  locationOptions.map((loc) => (
                    <label key={loc} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={locationFilters.includes(loc)}
                        onChange={() => onToggleLocation(loc)}
                        className="rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]/30"
                      />
                      {loc}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClearAll}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            Clear all
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#059669] hover:bg-[#047857]"
          >
            Apply filter
          </button>
        </div>
      </div>
    </div>
  );
}

function EmployerJobs() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [justPostedJob, setJustPostedJob] = useState(null);
  const [fcLoadingJobId, setFcLoadingJobId] = useState(null);
  const [fcError, setFcError] = useState('');
  const [message, setMessage] = useState('');
  const [limitInfo, setLimitInfo] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [statusFilters, setStatusFilters] = useState([]);
  const [locationFilters, setLocationFilters] = useState([]);
  const [showAllFiltersModal, setShowAllFiltersModal] = useState(false);
  const [jobCategories, setJobCategories] = useState([]);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState('');
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddSubcategoryForm, setShowAddSubcategoryForm] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  const [closeTargetJobId, setCloseTargetJobId] = useState(null);
const [closeReason, setCloseReason] = useState('Position filled');
const [closeOtherReason, setCloseOtherReason] = useState('');
const [closingJob, setClosingJob] = useState(false);
  const navigate = useNavigate();

    const [verificationStatus, setVerificationStatus] = useState(null); // null = still loading
  const isVerified = verificationStatus === 'verified';

  const [editingJobId, setEditingJobId] = useState(null);
  const [formData, setFormData] = useState(() => buildEmptyForm(user));

  // Right sidebar: current subscription plan, same source EmployerBilling.jsx
  // reads from. null = still loading, false = fetch failed.
  const [planInfo, setPlanInfo] = useState(null);
  const [planPrice, setPlanPrice] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [boostingJobId, setBoostingJobId] = useState(null);

  // Mobile-only: stats/filters panel is collapsed behind a toggle, same
  // pattern used in JobSearch.jsx for its filters drawer.
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

 const [skillSuggestions, setSkillSuggestions] = useState([]);
const [loadingSkills, setLoadingSkills] = useState(false);
const [showSkillDropdown, setShowSkillDropdown] = useState(false);
const skillDebounceRef = useRef(null);

const [locationSuggestions, setLocationSuggestions] = useState([]);
const [loadingLocation, setLoadingLocation] = useState(false);
const [showLocationDropdown, setShowLocationDropdown] = useState(false);
const [educationSuggestions, setEducationSuggestions] = useState([]);
const [loadingEducation, setLoadingEducation] = useState(false);
const [showEducationDropdown, setShowEducationDropdown] = useState(false);
const educationDebounceRef = useRef(null);
const locationDebounceRef = useRef(null);

const [roleSuggestions, setRoleSuggestions] = useState([]);
const [loadingRole, setLoadingRole] = useState(false);
const [showRoleDropdown, setShowRoleDropdown] = useState(false);
const roleDebounceRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      setJobs(response.data);
    } catch (err) {
      console.error('Failed to fetch jobs', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/jobs/employer/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get('/company/verification-status');
      setVerificationStatus(response.data.status);
    } catch (err) {
      console.error('Failed to fetch verification status', err);
      setVerificationStatus('unverified'); // fallback so UI doesn't hang in null forever
    }
  };

  const fetchPlan = async () => {
  try {
    const response = await api.get('/company/subscription');
    setPlanInfo(response.data);

    // Price isn't on the subscription response, so look it up from /plans by name.
    try {
      const plansRes = await api.get('/plans', { params: { type: 'subscription' } });
      const match = (plansRes.data || []).find(
        (p) => String(p.name).toLowerCase() === String(response.data?.plan_name || '').toLowerCase()
      );
      setPlanPrice(match ? Number(match.price_monthly) : null);
    } catch (e) {
      console.error('Failed to fetch plan price', e);
    }
  } catch (err) {
    console.error('Failed to fetch subscription', err);
    setPlanInfo(false);
  }
};

  const fetchWallet = async () => {
    try {
      const response = await api.get('/employer/wallet');
      setWallet(response.data);
    } catch (err) {
      console.error('Failed to fetch wallet', err);
    }
  };


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchJobs();
    fetchStats();
    fetchVerificationStatus();
    fetchPlan();
    fetchWallet();
    fetchJobCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Fetches the real category tree from /job-categories (backed by the
  // same business_categories table the admin panel manages) once on
  // mount, so the Job Category dropdown always reflects live data.
  const fetchJobCategories = async () => {
    try {
      const response = await api.get('/job-categories');
      setJobCategories(response.data || []);
    } catch (err) {
      console.error('Failed to load job categories', err);
    }
  };

  // Top-level category changed: store it as the job_category_id for now
  // (in case the chosen category has no subcategories), and remember it
  // separately so we can show/filter the subcategory dropdown.
  const handleCategoryChange = (parentId) => {
    setSelectedParentCategoryId(parentId);
    setFormData((prev) => ({ ...prev, job_category_id: parentId }));
  };

  // Subcategory changed: this becomes the actual job_category_id saved,
  // overriding the parent selection.
  const handleSubcategoryChange = (subcategoryId) => {
    setFormData((prev) => ({ ...prev, job_category_id: subcategoryId }));
  };

  // Employer adds a brand-new top-level category on the fly. Posts to the
  // same business_categories table the admin panel manages, then refetches
  // so the dropdown tree stays in sync with the DB (no local-only state).
  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setAddingCategory(true);
    try {
      const response = await api.post('/job-categories', { name });
      await fetchJobCategories();
      setSelectedParentCategoryId(String(response.data.id));
      setFormData((prev) => ({ ...prev, job_category_id: response.data.id }));
      setNewCategoryName('');
      setShowAddCategoryForm(false);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add category.');
    } finally {
      setAddingCategory(false);
    }
  };

  // Employer adds a new subcategory under the currently selected parent.
  const handleAddSubcategory = async () => {
    const name = newSubcategoryName.trim();
    if (!name || !selectedParentCategoryId) return;
    setAddingCategory(true);
    try {
      const response = await api.post('/job-categories', {
        name,
        parent_id: selectedParentCategoryId,
      });
      await fetchJobCategories();
      setFormData((prev) => ({ ...prev, job_category_id: response.data.id }));
      setNewSubcategoryName('');
      setShowAddSubcategoryForm(false);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add subcategory.');
    } finally {
      setAddingCategory(false);
    }
  };

  // Fills the description field with a draft generated from the other
  // fields already entered in the form. Employer can edit freely afterward.
  const handleAutoGenerateDescription = () => {
    const generated = generateJobDescription(formData);
    if (!generated) {
      setMessage('Fill in the job title first, then try again.');
      return;
    }
    setFormData((prev) => ({ ...prev, description: generated }));
    setMessage('');
  };

// ----- Job Title / Role: fetch suggestions as the user types -----
const handleTitleChange = (text) => {
  setFormData((prev) => ({ ...prev, title: text }));

  if (roleDebounceRef.current) clearTimeout(roleDebounceRef.current);

  if (text.trim().length < 2) {
    setRoleSuggestions([]);
    setShowRoleDropdown(false);
    return;
  }

  roleDebounceRef.current = setTimeout(async () => {
    setLoadingRole(true);
    try {
      const res = await api.post('/ai/suggest-keywords', { query: text });
      setRoleSuggestions(res.data.suggestions || []);
      setShowRoleDropdown(true);
    } catch (err) {
      console.error('Failed to fetch title suggestions', err);
    } finally {
      setLoadingRole(false);
    }
  }, 350);
};

const selectRole = (role) => {
  setFormData((prev) => ({ ...prev, title: role }));
  setShowRoleDropdown(false);
  setRoleSuggestions([]);
};
  

// ----- Location: fetch suggestions as the user types -----
const handleLocationChange = (text) => {
  setFormData((prev) => ({ ...prev, location: text }));

  if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);

  if (text.trim().length < 2) {
    setLocationSuggestions([]);
    setShowLocationDropdown(false);
    return;
  }

  locationDebounceRef.current = setTimeout(async () => {
    setLoadingLocation(true);
    try {
      const res = await api.post('/ai/suggest-cities', { query: text });
      setLocationSuggestions(res.data.suggestions || []);
      setShowLocationDropdown(true);
    } catch (err) {
      console.error('Failed to fetch location suggestions', err);
    } finally {
      setLoadingLocation(false);
    }
  }, 350); // debounce delay
};

const selectLocation = (loc) => {
  setFormData((prev) => ({ ...prev, location: loc }));
  setShowLocationDropdown(false);
  setLocationSuggestions([]);
};
const handleEducationChange = (text) => {
  setFormData((prev) => ({ ...prev, education: text }));
  if (educationDebounceRef.current) clearTimeout(educationDebounceRef.current);
  if (text.trim().length < 1) {
    setEducationSuggestions([]);
    setShowEducationDropdown(false);
    return;
  }
  educationDebounceRef.current = setTimeout(async () => {
    setLoadingEducation(true);
    try {
      const res = await api.post('/ai/suggest-education', { query: text });
      setEducationSuggestions(res.data.suggestions || []);
      setShowEducationDropdown(true);
    } catch (err) {
      console.error('Failed to fetch education suggestions', err);
    } finally {
      setLoadingEducation(false);
    }
  }, 350);
};
const selectEducation = (edu) => {
  setFormData((prev) => {
    const parts = String(prev.education || '').split(',').map((x) => x.trim());
    parts.pop();
    parts.push(edu);
    return { ...prev, education: parts.filter(Boolean).join(', ') };
  });
  setShowEducationDropdown(false);
  setEducationSuggestions([]);
};

// ----- Skills: fetch suggestions based on the segment being typed -----
const handleSkillsChange = (e) => {
  const text = e.target.value;
  setFormData((prev) => ({ ...prev, skills_required: text }));

  // only autocomplete the last (currently-typed) comma-separated segment
  const segments = text.split(',');
  const current = segments[segments.length - 1].trim();

  if (skillDebounceRef.current) clearTimeout(skillDebounceRef.current);

  if (current.length < 2) {
    setSkillSuggestions([]);
    setShowSkillDropdown(false);
    return;
  }

  skillDebounceRef.current = setTimeout(async () => {
    setLoadingSkills(true);
    try {
      const existing = segments.slice(0, -1).map((s) => s.trim()).filter(Boolean);
      const res = await api.post('/ai/suggest-keywords', { query: current });
      // suggestKeywords doubles as skill/title autocomplete; filter out already-added ones
      setSkillSuggestions((res.data.suggestions || []).filter((s) => !existing.includes(s)));
      setShowSkillDropdown(true);
    } catch (err) {
      console.error('Failed to fetch skill suggestions', err);
    } finally {
      setLoadingSkills(false);
    }
  }, 350);
};

const selectSkill = (skill) => {
  const segments = formData.skills_required.split(',');
  segments[segments.length - 1] = ` ${skill}`; // replace the in-progress segment
  const cleaned = segments.map((s) => s.trim()).filter(Boolean).join(', ');
  setFormData((prev) => ({ ...prev, skills_required: cleaned + ', ' }));
  setShowSkillDropdown(false);
  setSkillSuggestions([]);
};

  // Toggles a status in/out of the multi-select statusFilters array.
  const toggleStatusFilter = (value) => {
    setStatusFilters((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );
  };

  // Toggles a location in/out of the multi-select locationFilters array.
  const toggleLocationFilter = (value) => {
    setLocationFilters((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );
  };

  const handleEditClick = (job) => {
    setEditingJobId(job.id);
    setFormData({
      title: job.title || '',
      description: job.description || '',
      // Always use the employer's current profile name, not whatever was
      // stored on the job at the time it was posted (which may be stale
      // or reflect a since-changed company name).
      company_name: user?.name || job.company_name || '',
      location: job.location || '',
      work_mode: job.work_mode || 'office',
      job_type: job.job_type || 'full_time',
      work_shift: job.work_shift || 'day',
      skills_required: job.skills_required || '',
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      experience_required: job.experience_required ?? '',
      openings: job.openings || '',
      benefits: job.benefits || '',
      contact_number: job.contact_number || '',
      education: job.education || '',
    });
    setShowForm(true);
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Runs the limit check the instant "Post a new job" is clicked, so the
  // upgrade/credits modal can show immediately instead of only after the
  // user fills the whole form and hits submit.
  const handlePostNewJobClick = async () => {
    if (showForm) {
      handleCancelForm();
      return;
    }
    setCheckingLimit(true);
    try {
      const res = await api.get('/jobs/employer/can-post');
      if (res.data?.can_post === false) {
        setLimitInfo(res.data);
      } else {
        setShowForm(true);
      }
    } catch (err) {
      // If the check itself fails, don't block the employer — fall back to
      // opening the form as before; store() will still enforce the limit.
      setShowForm(true);
    } finally {
      setCheckingLimit(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingJobId(null);
    setFormData(buildEmptyForm(user));
  };

  // Opens the candidate database filtered to this job. Phone numbers and
  // emails stay locked there until the employer spends credits.
  const handleFindCandidates = (job) => {
    setFcError('');
    navigate(`/employer/database?job=${job.id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      if (editingJobId) {
        await api.put(`/jobs/${editingJobId}`, { ...formData, experience_required: formData.experience_required === '' ? 0 : formData.experience_required });
        setMessage('Job updated successfully!');
      } else {
        const res = await api.post('/jobs', { ...formData, experience_required: formData.experience_required === '' ? 0 : formData.experience_required });
        setMessage('Job posted successfully!');
        setJustPostedJob(res.data?.job || null);
      }
      setShowForm(false);
      setEditingJobId(null);
      setFormData(buildEmptyForm(user));
      fetchJobs();
      fetchStats();
    } catch (err) {
      setLimitInfo(null);
      if (err.response?.data?.upgrade_required) {
        setLimitInfo(err.response.data);
        setMessage('');
      } else {
        setMessage(err.response?.data?.message || 'Failed to save job.');
      }
    }
  };

 const handleStatusChange = async (jobId, newStatus) => {
  // Closing a job asks for a reason first; other statuses update directly.
  if (newStatus === 'closed') {
    setCloseTargetJobId(jobId);
    setCloseReason('Position filled'); // default
    setCloseOtherReason('');
    return;
  }
  try {
    await api.put(`/jobs/${jobId}`, { status: newStatus });
    fetchJobs();
    fetchStats();
  } catch (err) {
    console.error('Failed to update status', err);
  }
};

const confirmCloseJob = async () => {
  const reason = closeReason === 'Other' ? closeOtherReason.trim() : closeReason;
  if (!reason) {
    alert('Please type your reason.');
    return;
  }
  setClosingJob(true);
  try {
    await api.put(`/jobs/${closeTargetJobId}`, { status: 'closed', close_reason: reason });
    setCloseTargetJobId(null);
    fetchJobs();
    fetchStats();
  } catch (err) {
    alert(err.response?.data?.message || 'Could not close the job. Please try again.');
  } finally {
    setClosingJob(false);
  }
};

  const handleBoost = async (jobId) => {
    setBoostingJobId(jobId);
    setMessage('');
    try {
      await api.post('/company/grants/apply-boost', { job_posting_id: jobId });
      setMessage('Job boosted successfully.');
      fetchJobs();
      fetchWallet();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not boost this job.');
    } finally {
      setBoostingJobId(null);
    }
  };

  const handleTogglePin = async (jobId, newPinned) => {
    try {
      await api.put(`/jobs/${jobId}`, { pinned: newPinned });
      fetchJobs();
    } catch (err) {
      console.error('Failed to update pin', err);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job posting? This cannot be undone.')) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      fetchJobs();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete job.');
    }
  };

  // Reposts an expired/closed job — adjust the endpoint to match your
  // backend route for reposting.
  const handleRepost = async (jobId) => {
    try {
      await api.put(`/jobs/${jobId}/repost`);
      setMessage('Job reposted successfully!');
      fetchJobs();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to repost job.');
    }
  };

  const locationOptions = useMemo(
    () => [...new Set(jobs.map((j) => j.location).filter(Boolean))],
    [jobs]
  );

  const filteredJobs = jobs.filter((j) => {
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(j.status);
    const matchesLocation = locationFilters.length === 0 || locationFilters.includes(j.location);
    return matchesStatus && matchesLocation;
  });

return (
  <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />
      <EmployerSidebar user={user} activeTab="jobs" fixed />

         <div className="lg:pl-64">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <EmployerSidebar user={user} activeTab="jobs" mobile />

            <div className="flex flex-col lg:flex-row gap-6 items-start">
              {/* Main column */}
              <div className="flex-1 w-full min-w-0">
          {/* Header */}
                   <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6">
            <h1 className={`text-2xl font-bold ${GRADIENT_TEXT}`}>
              All Jobs ({jobs.length})
            </h1>

            {isVerified ? (
              <button
                onClick={handlePostNewJobClick}
                disabled={checkingLimit}
                className={`mt-4 lg:mt-0 inline-flex items-center gap-2 px-6 py-3 ${GRADIENT_BUTTON} ${checkingLimit ? "opacity-60 cursor-wait" : ""}`}
              >
                {checkingLimit ? "Checking..." : (showForm ? "Cancel" : "Post a new job")}
                {!showForm && !checkingLimit && <ChevronDownIcon className="w-4 h-4" />}
              </button>
            ) : null}
            {!isVerified && verificationStatus !== null ? (
              <Link
                to="/company/verification"
                className="mt-4 lg:mt-0 inline-flex items-center gap-2 px-6 py-3 rounded-[18px] bg-gray-100 text-gray-500 font-semibold text-sm hover:bg-gray-200 transition"
              >
                🛡️ Complete verification to post jobs
              </Link>
            ) : null}
          </div>

          {verificationStatus !== null && !isVerified && (
            <div className="bg-[#FFFBEB] border border-[#F59E0B]/30 text-[#92400E] px-4 py-3 rounded-xl text-sm mb-6 flex items-center justify-between gap-3 flex-wrap">
              <span>
                Your company isn't verified yet — verified employers can post jobs. Submit your GST/PAN or verify your official email to get started.
              </span>
              <Link to="/company/verification" className="font-semibold underline whitespace-nowrap">
                Go to Verification
              </Link>
            </div>
          )}

       {/* Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <FilterPill
              icon={<FunnelIcon />}
              label="All Filters"
              count={statusFilters.length + locationFilters.length || undefined}
              active={showAllFiltersModal || statusFilters.length + locationFilters.length > 0}
              clearable={false}
              onClick={() => setShowAllFiltersModal(true)}
            />

            <FilterPill
              icon={<PlusIcon />}
              label="Active"
              count={stats?.active_jobs || 0}
              active={statusFilters.includes('active')}
              onClick={() => toggleStatusFilter('active')}
            />

            <FilterPill
              icon={<PlusIcon />}
              label="Under Review"
              count={stats?.under_review_jobs || 0}
              active={statusFilters.includes('under_review')}
              onClick={() => toggleStatusFilter('under_review')}
            />

            <FilterPill
  icon={<PlusIcon />}
  label="Closed"
  count={stats?.closed_jobs || 0}
  active={statusFilters.includes('closed')}
  onClick={() => toggleStatusFilter('closed')}
/>

<FilterPill
  icon={<PlusIcon />}
  label="Expired"
  count={stats?.expired_jobs || 0}
  active={statusFilters.includes('expired')}
  onClick={() => toggleStatusFilter('expired')}
/>

            {/* <FilterPill
              icon={<PlusIcon />}
              label="Select Plan"
              count={0}
              active={false}
              clearable={false}
              onClick={() => {}}
            /> */}
          </div>

          <AllFiltersModal
            show={showAllFiltersModal}
            onClose={() => setShowAllFiltersModal(false)}
            statusFilters={statusFilters}
            onToggleStatus={toggleStatusFilter}
            locationFilters={locationFilters}
            onToggleLocation={toggleLocationFilter}
            locationOptions={locationOptions}
            onClearAll={() => { setStatusFilters([]); setLocationFilters([]); }}
          />

{closeTargetJobId && (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
    onClick={() => !closingJob && setCloseTargetJobId(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-bold text-[#0D1B3D]">Close this job</h3>
      <p className="text-sm text-gray-500 mt-1">
        Tell us why you are closing this position. Candidates will no longer be able to apply.
      </p>

      <label className="block text-sm font-medium text-[#0D1B3D] mt-4 mb-1">Reason</label>
      <select
        value={closeReason}
        onChange={(e) => setCloseReason(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
      >
        {CLOSE_REASONS.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>

      {closeReason === 'Other' && (
        <textarea
          value={closeOtherReason}
          onChange={(e) => setCloseOtherReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Type your reason..."
          className="w-full mt-3 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
        />
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button
          onClick={() => setCloseTargetJobId(null)}
          disabled={closingJob}
          className="px-4 py-2 text-sm font-medium rounded-lg border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={confirmCloseJob}
          disabled={closingJob}
          className={`px-4 py-2 text-sm ${GRADIENT_BUTTON} disabled:opacity-50`}
        >
          {closingJob ? 'Closing...' : 'Close job'}
        </button>
      </div>
    </div>
  </div>
)}

          {(statusFilters.length > 0 || locationFilters.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
              {statusFilters.map((s) => (
                <span key={`status-${s}`} className="inline-flex items-center gap-1.5 bg-[#EFF6FF] text-[#2563EB] rounded-full px-3 py-1 font-medium">
                  {s.replace('_', ' ')}
                  <button onClick={() => toggleStatusFilter(s)} className="hover:text-[#1D4ED8]">&times;</button>
                </span>
              ))}
              {locationFilters.map((l) => (
                <span key={`loc-${l}`} className="inline-flex items-center gap-1.5 bg-[#EFF6FF] text-[#2563EB] rounded-full px-3 py-1 font-medium">
                  {l}
                  <button onClick={() => toggleLocationFilter(l)} className="hover:text-[#1D4ED8]">&times;</button>
                </span>
              ))}
              <button
                onClick={() => { setStatusFilters([]); setLocationFilters([]); }}
                className="text-[#059669] font-semibold hover:underline"
              >
                Clear All
              </button>
            </div>
          )}

          {(statusFilters.length > 0 || locationFilters.length > 0) && (
            <p className="text-sm text-gray-500 mb-3">
              Showing {filteredJobs.length} job{filteredJobs.length === 1 ? '' : 's'} as per filters
            </p>
          )}

          {/* Mobile-only: nav drawer
          <button
            onClick={() => setShowFiltersMobile((s) => !s)}
            className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
          >
            <span>☰ Menu</span>
            <span className="text-sm text-gray-400 inline-flex items-center gap-1">
              {showFiltersMobile ? 'Hide' : 'Show'}
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
            </span>
          </button>

          {showFiltersMobile && (
            <div className="lg:hidden bg-white rounded-xl shadow p-3 space-y-1 mb-4">
              <SidebarNavItem icon={<BriefcaseNavIcon />} label="Jobs" to="/employer/jobs" active />
              <SidebarNavItem icon={<DatabaseNavIcon />} label="Database" to="/employer/jobs" />
              <SidebarNavItem icon={<UsersNavIcon />} label="Candidate Pipeline" to="/employer/pipeline" />
              <SidebarNavItem icon={<BarChartNavIcon />} label="Reports" to="/analytics/hiring" />
              <div className="my-2 border-t border-gray-100" />
              <SidebarNavItem icon={<HelpNavIcon />} label="Help & Support" disabled />
              <SidebarNavItem icon={<PhoneNavIcon />} label="Contact Sales" disabled />
            </div>
          )} */}

          {/* Job Form + List */}
          <div className="space-y-4">
            {limitInfo && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
                onClick={() => setLimitInfo(null)}
              >
                <div
                  className="relative w-full max-w-lg overflow-hidden rounded-2xl border-2 border-[#7C3AED] bg-gradient-to-br from-[#EFF6FF] via-[#F5F3FF] to-[#FFF7ED] p-6 shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#7C3AED] text-white text-2xl flex-shrink-0">
                      &#128274;
                    </span>
                    <div className="flex-1">
                      <p className="text-lg font-extrabold text-[#0D1B3D] mb-1">
                        You've used all {limitInfo.job_post_limit} job post{limitInfo.job_post_limit === 1 ? '' : 's'} on your {limitInfo.plan_name} plan
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        Upgrade your plan for more monthly job posts, or buy a Hiring Pack or Pay-Per-Job pack to post right now.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => navigate('/employer/settings/billing')}
                          className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white font-bold px-5 py-2.5 rounded-xl hover:shadow-lg transition"
                        >
                          Upgrade Plan
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/employer/settings/billing?tab=hiring')}
                          className="border-2 border-[#7C3AED] text-[#7C3AED] font-bold px-5 py-2.5 rounded-xl hover:bg-white transition"
                        >
                          Hiring Packs
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/employer/settings/billing?tab=payperjob')}
                          className="border-2 border-[#7C3AED] text-[#7C3AED] font-bold px-5 py-2.5 rounded-xl hover:bg-white transition"
                        >
                          Pay-Per-Job
                        </button>
                        <button
                          type="button"
                          onClick={() => setLimitInfo(null)}
                          className="text-sm text-gray-400 hover:text-gray-600 px-2 py-2.5"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {message && (
              <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg text-sm font-medium">
                {message}
              </div>
            )}

            {justPostedJob && (
              <div className="bg-white border border-[#7C3AED]/20 rounded-xl shadow p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#0D1B3D]">Want candidates for "{justPostedJob.title}" right now?</p>
                  <p className="text-sm text-gray-500">Skip the wait — get matched candidates from our database instantly.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleFindCandidates(justPostedJob)}
                    disabled={fcLoadingJobId === justPostedJob.id}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 ${GRADIENT_BUTTON} disabled:opacity-50`}
                  >
                    {fcLoadingJobId === justPostedJob.id ? 'Opening...' : 'Find Candidates Right Now'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setJustPostedJob(null)}
                    className="text-sm text-gray-400 hover:text-gray-600 font-semibold"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            )}

            {fcError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium">
                {fcError}
              </div>
            )}

            {showForm && (
              <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-xl shadow space-y-4">
                <div>
                  <h3 className="font-bold text-[#0D1B3D] text-lg">
                    {editingJobId ? 'Edit Job' : 'Post New Job'}
                  </h3>
                  <p className="text-sm text-gray-400 mt-0.5">
                    Fill in the details below — required fields are marked.
                  </p>
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-base">💼</span>
                    <h4 className="text-sm font-semibold text-gray-700">Basic details</h4>
                  </div>

                <div className="relative">
  <label className="block text-xs font-medium text-gray-500 mb-1">Job title *</label>
  <input
    type="text"
    name="title"
    placeholder="Job Title"
    value={formData.title}
    onChange={(e) => handleTitleChange(e.target.value)}
    onFocus={() => formData.title.length >= 2 && setShowRoleDropdown(true)}
    onBlur={() => setTimeout(() => setShowRoleDropdown(false), 150)}
    required
    className="w-full border rounded-lg px-3 py-2"
  />
  {loadingRole && (
    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Loading…</span>
  )}
  {showRoleDropdown && roleSuggestions.length > 0 && (
    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
      {roleSuggestions.map((role, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={() => selectRole(role)}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50"
        >
          💼 {role}
        </button>
      ))}
    </div>
  )}
</div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Company name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    readOnly
                    required
                    className="w-full border rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Pulled from your company profile — update it on Company Profile if it's wrong.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-500">Job Category</label>
                    <button
                      type="button"
                      onClick={() => setShowAddCategoryForm((prev) => !prev)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {showAddCategoryForm ? 'Cancel' : '+ Add new category'}
                    </button>
                  </div>
                  {showAddCategoryForm ? (
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="New category name"
                        className="flex-1 border rounded-lg px-3 py-2.5 text-sm bg-white"
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={addingCategory || !newCategoryName.trim()}
                        className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50"
                      >
                        {addingCategory ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  ) : null}
                  <select
                    value={selectedParentCategoryId}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="">Select a category</option>
                    {jobCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {(() => {
                  const parentCategory = jobCategories.find((c) => String(c.id) === String(selectedParentCategoryId));
                  const subcategories = parentCategory?.children || [];
                  if (!parentCategory) return null;
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-500">Subcategory</label>
                        <button
                          type="button"
                          onClick={() => setShowAddSubcategoryForm((prev) => !prev)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {showAddSubcategoryForm ? 'Cancel' : '+ Add new subcategory'}
                        </button>
                      </div>
                      {showAddSubcategoryForm ? (
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={newSubcategoryName}
                            onChange={(e) => setNewSubcategoryName(e.target.value)}
                            placeholder="New subcategory name"
                            className="flex-1 border rounded-lg px-3 py-2.5 text-sm bg-white"
                          />
                          <button
                            type="button"
                            onClick={handleAddSubcategory}
                            disabled={addingCategory || !newSubcategoryName.trim()}
                            className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50"
                          >
                            {addingCategory ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      ) : null}
                      {subcategories.length > 0 ? (
                        <select
                          value={formData.job_category_id}
                          onChange={(e) => handleSubcategoryChange(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm bg-white"
                        >
                          <option value={parentCategory.id}>General {parentCategory.name}</option>
                          {subcategories.map((sub) => (
                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-xs text-gray-400">No subcategories yet — add one above, or leave this category as-is.</p>
                      )}
                    </div>
                  );
                })()}

         <div className="relative">
  <label className="block text-xs font-medium text-gray-500 mb-1">Location</label>
  <input
    type="text"
    value={formData.location}
    onChange={(e) => handleLocationChange(e.target.value)}
    onFocus={() => formData.location.length >= 2 && setShowLocationDropdown(true)}
    onBlur={() => setTimeout(() => setShowLocationDropdown(false), 150)}
    placeholder="Location (e.g. Bengaluru)"
    className="w-full border rounded-lg px-3 py-2"
  />
  {loadingLocation && (
    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Loading…</span>
  )}
  {showLocationDropdown && locationSuggestions.length > 0 && (
    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
      {locationSuggestions.map((loc, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={() => selectLocation(loc)}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50"
        >
          📍 {loc}
        </button>
      ))}
    </div>
  )}
</div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Mobile number</label>
                  <input
                    type="tel"
                    name="contact_number"
                    placeholder="Mobile Number (for Call / WhatsApp)"
                    value={formData.contact_number}
                    onChange={handleChange}
                    pattern="[0-9]{10,15}"
                    title="Enter a valid mobile number (digits only)"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Shown to job seekers as Call / WhatsApp options on this job's page.
                  </p>
                </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Work mode</label>
                      <select name="work_mode" value={formData.work_mode} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="office">Office</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="work_from_home">Work From Home</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Job type</label>
                      <select name="job_type" value={formData.job_type} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="full_time">Full Time</option>
                        <option value="part_time">Part Time</option>
                        <option value="internship">Internship</option>
                        <option value="contract">Contract</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Shift</label>
                      <select name="work_shift" value={formData.work_shift} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="day">Day Shift</option>
                        <option value="night">Night Shift</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🎯</span>
                    <h4 className="text-sm font-semibold text-gray-700">Candidate requirements</h4>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                      <select name="gender" value={formData.gender} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="both">Any</option>
                        <option value="male">Male only</option>
                        <option value="female">Female only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Marital status</label>
                      <select name="marital_status" value={formData.marital_status} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="both">Any</option>
                        <option value="married">Married only</option>
                        <option value="unmarried">Unmarried only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">English level</label>
                      <select name="english_level" value={formData.english_level} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition">
                        <option value="not_required">Not required</option>
                        <option value="basic_required">Basic required</option>
                        <option value="fluent_required">Fluent required</option>
                      </select>
                    </div>
                  </div>

                <div className="relative">
  <label className="block text-xs font-medium text-gray-500 mb-1">Education *</label>
  <input
    type="text"
    value={formData.education}
    onChange={(e) => handleEducationChange(e.target.value)}
    onFocus={() => formData.education.length >= 1 && setShowEducationDropdown(true)}
    onBlur={() => setTimeout(() => setShowEducationDropdown(false), 150)}
    placeholder="Education requirement (e.g. Graduate, 12th Pass, B.Tech)"
    required
    className="w-full border rounded-lg px-3 py-2"
  />
  {loadingEducation && (
    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Loading…</span>
  )}
  {showEducationDropdown && educationSuggestions.length > 0 && (
    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
      {educationSuggestions.map((edu, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={() => selectEducation(edu)}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50"
        >
          🎓 {edu}
        </button>
      ))}
    </div>
  )}
</div>


                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Min age</label>
                      <input type="number" name="age_min" placeholder="18" min="14" max="100" value={formData.age_min} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Max age</label>
                      <input type="number" name="age_max" placeholder="45" min="14" max="100" value={formData.age_max} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-base">💰</span>
                    <h4 className="text-sm font-semibold text-gray-700">Compensation and skills</h4>
                  </div>

             <div className="relative">
  <label className="block text-xs font-medium text-gray-500 mb-1">Skills required</label>
  <input
    type="text"
    name="skills_required"
    placeholder="Skills (e.g. PHP, Laravel, MySQL)"
    value={formData.skills_required}
    onChange={handleSkillsChange}
    onFocus={() => skillSuggestions.length > 0 && setShowSkillDropdown(true)}
    onBlur={() => setTimeout(() => setShowSkillDropdown(false), 150)}
    className="w-full border rounded-lg px-3 py-2"
  />
  {loadingSkills && (
    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Loading…</span>
  )}
  {showSkillDropdown && skillSuggestions.length > 0 && (
    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
      {skillSuggestions.map((skill, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={() => selectSkill(skill)}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50"
        >
          🧩 {skill}
        </button>
      ))}
    </div>
  )}
</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Min salary</label>
                      <input type="number" name="salary_min" placeholder="15000" value={formData.salary_min} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Max salary</label>
                      <input type="number" name="salary_max" placeholder="25000" value={formData.salary_max} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Experience (years)</label>
                      <input type="number" name="experience_required" placeholder="0" value={formData.experience_required} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Openings</label>
                      <input type="number" name="openings" placeholder="1" value={formData.openings} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Benefits</label>
                    <textarea name="benefits" placeholder="PF, health insurance, incentives…" value={formData.benefits} onChange={handleChange} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" />
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base">📝</span>
                      <h4 className="text-sm font-semibold text-gray-700">Description</h4>
                    </div>
                    <button
                      type="button"
                      onClick={handleAutoGenerateDescription}
                      className="text-xs font-semibold text-[#2563EB] hover:underline"
                    >
                      ✨ Auto-generate from details above
                    </button>
                  </div>
                  <textarea
                    name="description"
                    placeholder="Job Description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Fill in the fields above, then click "Auto-generate" for a starting draft — you can edit it freely afterward.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                <button type="submit" className={`w-full sm:w-auto px-6 py-2.5 ${GRADIENT_BUTTON}`}>
  {editingJobId ? 'Update Job' : 'Post Job'}
</button>
                  <button
                    type="button"
                    onClick={handleCancelForm}
                    className="w-full sm:w-auto text-gray-500 font-semibold px-6 py-2 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {loading ? (
              <p className="text-gray-500">Loading...</p>
            ) : filteredJobs.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">
                No jobs found for this filter.
              </div>
            ) : (
              filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  user={user}
                  onEdit={handleEditClick}
                  onDelete={handleDelete}
                  onStatusChange={handleStatusChange}
                  onTogglePin={handleTogglePin}
                  onBoost={handleBoost}
                  boostingJobId={boostingJobId}
                  boostsAvailable={wallet?.featured_jobs_remaining ?? 0}
                  onRepost={handleRepost}
                  onFindCandidates={handleFindCandidates}
                  fcLoadingJobId={fcLoadingJobId}
                />
              ))
            )}
          </div>
              </div>
              {/* End main column */}

              {/* Right sidebar */}
              <div className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-8">
                <CurrentPlanCard planInfo={planInfo} planPrice={planPrice} />
              </div>
            </div>
          </div>
        </div>
      </div>

  );
}

export default EmployerJobs;